import * as React from 'react';
import Box from '@mui/material/Box';

import isPointerInside from 'shared/helpers/isPointerInside';
import { useEvent, useHoverDirty } from 'react-use';
import mergeRefs from 'merge-refs';

const ResponsiveDrawer: React.FC<Props> = React.forwardRef(
    (props, ref): React.ReactElement => {
        const {
            Footer,
            children,
            onClose,
            onSave,
            onSubmit,
            onZoomChanged,
            onBack,
            onModalChanged,
            onFailValidation,
            documentRef,
            innerModalCompletedRef,
            isDocumentSuccess,
            imageContentRef,
            categoryMenuName,
            categoryDefaultValue,
            documentPlaceholder,
            innerRef,
            isLoading,
            canCategorize,
            unexpectedType,
            updateGroup,
            isFullScreen,
            setFullScreen,
            allowRerun,
            allowRerunDuplication,
            allowRevalidation,
            isActiveReworks,
            allowRerunGrouping,
            mainContainerRef,
        } = props;
        const footerRef = React.useRef<Element>(null);
        const isHovering = useHoverDirty(footerRef);
        const innerContentWrapperRef = React.useRef<HTMLElement | null>(null);
        const [scrollBarHeight, setScrollBarHeight] = React.useState<number>(0);
        const [isZoomChanged, setZoomChanged] = React.useState<boolean>(false);

        useEvent('resize', () => {
            const { current: contentWrapper } = innerContentWrapperRef;
            if (contentWrapper) {
                const scrollbarHeight =
                    contentWrapper.offsetHeight - contentWrapper.clientHeight;
                setScrollBarHeight(scrollbarHeight);
            }
        });
        React.useLayoutEffect(() => {
            if (isZoomChanged) {
                const { current: contentWrapper } = innerContentWrapperRef;
                if (contentWrapper) {
                    const scrollbarHeight =
                        contentWrapper.offsetHeight -
                        contentWrapper.clientHeight;
                    setScrollBarHeight(scrollbarHeight);
                }

                setZoomChanged(false);
            }
        }, [isZoomChanged]);
        const handleZoomChanged = React.useCallback(
            (val: number) => {
                setZoomChanged(true);
                if (typeof onZoomChanged === 'function') {
                    onZoomChanged(val);
                }
            },
            [onZoomChanged]
        );
        const footerContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    bottom: `${scrollBarHeight}px !important`,
                    width: isFullScreen ? '270px' : 'auto    ',
                    alignSelf: isFullScreen ? 'end' : 'initial',
                };
            }
            return {
                bottom: `${scrollBarHeight}px !important`,
            };
        }, [isFullScreen, scrollBarHeight]);

        const mainContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    backgroundColor: isFullScreen
                        ? '#F6F6F6 !important'
                        : 'transparent',
                };
            }
            return {
                backgroundColor: '#F6F6F6 !important',
            };
        }, [isFullScreen]);
        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * Check pointer is on footer area
                 * @param {MouseEvent} event - event object
                 * @return {boolean} condition to check mouse position inside footer container.
                 */
                isPointerOnFooter: (event: any) => {
                    const { current: element } = footerRef;
                    const { current: contentWrapper } = innerContentWrapperRef;
                    if (contentWrapper) {
                        if (
                            event.y > contentWrapper.clientHeight &&
                            event.y < contentWrapper.offsetHeight
                        ) {
                            return true;
                        }
                    }
                    if (element) return isPointerInside(element, event);
                    return isHovering;
                },
            }),
            [isHovering]
        );

        return (
            <Box
                className="footer-wrapper not-expected-container"
                position="relative"
            >
                <Box
                    ref={mergeRefs(mainContainerRef, innerContentWrapperRef)}
                    component="main"
                    aria-label="container with footing menu"
                    className="main-content"
                    sx={mainContainerSX}
                >
                    {children}
                </Box>

                <Box
                    component="nav"
                    className="footer-container"
                    ref={footerRef}
                    sx={footerContainerSX}
                >
                    <Footer
                        ref={innerRef}
                        allowRerun={allowRerun}
                        isLoading={isLoading}
                        onModalChanged={onModalChanged}
                        onClose={onClose}
                        onSave={onSave}
                        allowRevalidation={allowRevalidation}
                        onBack={onBack}
                        onZoomChanged={handleZoomChanged}
                        onSubmit={onSubmit}
                        onFailValidation={onFailValidation}
                        canCategorize={canCategorize}
                        documentRef={documentRef}
                        isFullScreen={isFullScreen}
                        setFullScreen={setFullScreen}
                        documentPlaceholder={documentPlaceholder ?? ''}
                        isDocumentSuccess={isDocumentSuccess}
                        categoryDefaultValue={categoryDefaultValue ?? ''}
                        categoryMenuName={categoryMenuName ?? ''}
                        innerModalCompletedRef={innerModalCompletedRef}
                        unexpectedType={unexpectedType}
                        allowRerunDuplication={allowRerunDuplication}
                        updateGroup={updateGroup}
                        isActiveReworks={isActiveReworks}
                        allowRerunGrouping={allowRerunGrouping}
                        imageContentRef={imageContentRef}
                    />
                </Box>
            </Box>
        );
    }
);

export default ResponsiveDrawer;
